"use client";

import {
  Box,
  Text,
  VStack,
  Button,
  HStack,
  Separator,
  Stack,
} from "@chakra-ui/react";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { useRag } from "@/state/globalStore";
import { useOriginActions } from "@/features/origins";
import useChatBot from "../hooks/useChatBot";
import {
  bundleMessagesForOriginStorage,
  copySelectedMessagesToClipboard,
} from "../utils";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import type { Target } from "@/generated/fastapi";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import { useTargetItems, useTargetsQuery } from "@/features/targets";

interface ChatBotSettingsProps {
  settingsState: "closed" | "half" | "full"; // Settings drawer state: closed, half, full
  setInputQuery: (query: string) => void;
  referedTargets: Target[];
  setReferedTargets: (ids: Target[]) => void;
}

export default function ChatBotSettings({
  settingsState,
  setInputQuery,
  // Optional props for target selection
  referedTargets,
  setReferedTargets,
}: ChatBotSettingsProps) {
  const {
    chatMessages,
    deleteChatMessage,
    selectedChatMessages,
    clearSelectedChatMessages,
    countSelectedChatMessages,
    addChatMessage,
  } = useRag();
  const {
    handleSendMessage,
    handleLoadKnowledgeClick,
    handleClearKnowledgeClick,
  } = useChatBot();
  const { handleAddNewOriginClick } = useOriginActions();
  const targetsQuery = useTargetsQuery();
  const targetItems = useTargetItems(targetsQuery.data);

  // const [referedTargetIds, setReferedTargetIds] = useState<string[]>([]); // keep list to be compatible with GenericSelector

  return (
    <VStack gap="4" align="stretch">
      {/* Basic Settings - Visible at HALF state */}
      {(settingsState === "half" || settingsState === "full") && (
        <Box
          p="2"
          border="sm"
          borderRadius="md"
          borderColor="moveon.secondaryLight"
        >
          <Text fontWeight="bold" fontSize="sm">
            Quick Actions
          </Text>

          <VStack gap="2" align="stretch">
            <Stack gap="2" direction={{ base: "column", md: "row" }}>
              {/* Help */}
              <Button
                flex="1"
                // onClick={() => handleSendMessage("/help")}
                onClick={() => handleSendMessage({ text: "/help" })}
                // @ts-ignore Chakra complains about borderWidth
                borderWidth="1px"
                variant="ghost"
              >
                <HStack gap="1">
                  <GenericIcon icon="help" size={iconSizePresets.menuItem} />
                  <Text fontSize="sm">Help</Text>
                </HStack>
              </Button>
              {/* Clear textarea */}
              <Button
                flex="1"
                onClick={() => setInputQuery("")}
                // @ts-ignore Chakra complains about borderWidth
                borderWidth="1px"
                variant="ghost"
              >
                <HStack gap="1">
                  <GenericIcon icon="clear" size={iconSizePresets.menuItem} />
                  <Text fontSize="sm">Clear Input</Text>
                </HStack>
              </Button>
              {/* Reset Chat */}
              <Button
                flex="1"
                // onClick={() => handleSendMessage("/reset")}
                onClick={() => handleSendMessage({ text: "/reset" })}
                // @ts-ignore Chakra complains about borderWidth
                borderWidth="1px"
                variant="ghost"
              >
                <HStack gap="1">
                  <GenericIcon icon="reset" size={iconSizePresets.menuItem} />
                  <Text fontSize="sm">Reset Chat</Text>
                </HStack>
              </Button>
            </Stack>

            <GenericSelector<Target>
              previewItems={targetItems}
              stateObject={referedTargets}
              stateObjectSetter={setReferedTargets}
              sourceItems={targetsQuery.data ?? []}
              mode="select"
              placeholder="Choose target to refer the bot to..."
            />

            {/* Selection Actions - Only show when messages are selected */}
            {countSelectedChatMessages() > 0 && (
              <>
                <Separator />
                <Text fontSize="xs" fontWeight="semibold">
                  Selection Actions ({countSelectedChatMessages()} message
                  selected)
                </Text>

                <Stack direction={{ base: "column", md: "row" }} gap="2">
                  {/* Add as Origin */}
                  <Button
                    size="sm"
                    flex="1"
                    onClick={async () => {
                      await handleAddNewOriginClick({
                        raw_text_input: bundleMessagesForOriginStorage(
                          chatMessages.filter((msg) =>
                            selectedChatMessages.includes(msg.id),
                          ),
                        ),
                        sourced_where: "chatbot",
                      });
                    }}
                    variant="ghost"
                  >
                    <GenericIcon icon="add" size={iconSizePresets.menuItem} />
                    <Text fontSize="sm">Add as source material</Text>
                  </Button>
                  {/* Copy to Clipboard */}
                  <Button
                    size="sm"
                    flex="1"
                    onClick={() => {
                      copySelectedMessagesToClipboard(
                        chatMessages.filter((msg) =>
                          selectedChatMessages.includes(msg.id),
                        ),
                      );
                    }}
                    variant="ghost"
                  >
                    <GenericIcon
                      icon="copy_clipboard"
                      size={iconSizePresets.menuItem}
                    />
                    <Text fontSize="sm">Copy to Clipboard</Text>
                  </Button>
                  {/* Delete Selection */}
                  <Button
                    size="sm"
                    flex="1"
                    onClick={() => {
                      selectedChatMessages.forEach((id) =>
                        deleteChatMessage(id),
                      );
                      clearSelectedChatMessages();
                    }}
                    variant="ghost"
                  >
                    <GenericIcon
                      icon="delete_permanent"
                      size={iconSizePresets.menuItem}
                    />
                    <Text fontSize="sm">Delete Selection</Text>
                  </Button>
                  {/* Unselect Selection */}
                  <Button
                    size="sm"
                    flex="1"
                    onClick={() => clearSelectedChatMessages()}
                    variant="ghost"
                  >
                    <GenericIcon
                      icon="unselect"
                      size={iconSizePresets.menuItem}
                    />
                    <Text fontSize="sm">Unselect</Text>
                  </Button>
                </Stack>
              </>
            )}
          </VStack>
        </Box>
      )}

      {/* Advanced Settings - Only visible at FULL state */}
      {settingsState === "full" && (
        <Box
          p="2"
          border="sm"
          borderRadius="md"
          borderColor="moveon.tertiaryLight"
        >
          <Text fontWeight="bold" fontSize="sm">
            Profile Management
          </Text>

          <Stack
            gap="2"
            direction={{ base: "column", md: "row" }}
            align="stretch"
          >
            {/* Reload profile knowledge */}
            <Button
              size="sm"
              onClick={async () => {
                // First clear, then load
                await handleClearKnowledgeClick();

                // Second, add system message into chat: Waiting for profile knowledge to reload...
                addChatMessage({
                  id: Date.now(), // simple unique id
                  sender: "system",
                  text: "Please wait for profile knowledge to reload...",
                  timestamp: new Date().toISOString(),
                });

                // Third, load knowledge
                await handleLoadKnowledgeClick();
              }}
              variant="ghost"
            >
              <HStack gap="2" justify="center" width="full">
                <GenericIcon icon="reload" size={iconSizePresets.menuItem} />
                <Text fontSize="sm">Reload Profile Knowledge</Text>
              </HStack>
            </Button>
          </Stack>
        </Box>
      )}
    </VStack>
  );
}
